import { useCallback, useEffect, useRef, useState } from 'react'

import type { MentionItem } from '@/components/stella/types'

export const DEFAULT_RAIL_WIDTH = 360

const STORAGE_KEY_OPEN = 'stella:isOpen'
const STORAGE_KEY_CONVERSATION = 'stella:currentConversation'
const STORAGE_KEY_RAIL_WIDTH = 'stella:railWidth'
const STORAGE_KEY_DRAFT = 'stella:draft'
const STORAGE_KEY_MENTIONS = 'stella:mentions'
const DRAFT_DEBOUNCE_MS = 300

const STORAGE_KEYS = [
  STORAGE_KEY_OPEN,
  STORAGE_KEY_CONVERSATION,
  STORAGE_KEY_RAIL_WIDTH,
  STORAGE_KEY_DRAFT,
  STORAGE_KEY_MENTIONS,
]

const readStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) {
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, value)
    }
  } catch (error) {
    console.warn('[useStellaSession] sessionStorage write failed:', error)
  }
}

export const clearStellaSessionStorage = () => {
  for (const key of STORAGE_KEYS) {
    writeStorage(key, null)
  }
}

const readMentions = (): MentionItem[] => {
  const stored = readStorage(STORAGE_KEY_MENTIONS)
  if (!stored) {
    return []
  }

  try {
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? (parsed as MentionItem[]) : []
  } catch {
    return []
  }
}

export interface StellaSession {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  railWidth: number
  setRailWidth: (width: number | ((previous: number) => number)) => void
  currentConversationId: string | null
  setCurrentConversationId: (id: string | null) => void
  initialDraft: string
  setInitialDraft: (value: string) => void
  setDraft: (value: string) => void
  setInitialMentions: (value: MentionItem[]) => void
  initialMentions: MentionItem[]
  initialValueVersion?: number
  setMentions: (mentions: MentionItem[]) => void
  initialDraftVersion: number
}

interface UseStellaSessionProps {
  defaultRailWidth?: number
}

export const useStellaSession = ({
  defaultRailWidth = DEFAULT_RAIL_WIDTH,
}: UseStellaSessionProps): StellaSession => {
  const [isOpen, setIsOpenState] = useState<boolean>(
    () => readStorage(STORAGE_KEY_OPEN) === 'true'
  )

  const [currentConversationId, setCurrentConversationIdState] = useState<
    string | null
  >(() => readStorage(STORAGE_KEY_CONVERSATION))

  const [railWidth, setRailWidthState] = useState<number>(() => {
    const stored = readStorage(STORAGE_KEY_RAIL_WIDTH)
    const parsed = stored !== null ? Number(stored) : NaN
    return Number.isFinite(parsed) ? parsed : defaultRailWidth
  })

  const initialDraftRef = useRef<string>(readStorage(STORAGE_KEY_DRAFT) ?? '')
  const initialMentionsRef = useRef<MentionItem[]>(readMentions())
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [initialDraftVersion, setInitialDraftVersion] = useState(0)

  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open)
    writeStorage(STORAGE_KEY_OPEN, open ? 'true' : null)
  }, [])

  const setCurrentConversationId = useCallback((id: string | null) => {
    setCurrentConversationIdState(id)
    writeStorage(STORAGE_KEY_CONVERSATION, id)
  }, [])

  const setRailWidth = useCallback(
    (width: number | ((previous: number) => number)) => {
      setRailWidthState((previous) => {
        const next = typeof width === 'function' ? width(previous) : width
        writeStorage(STORAGE_KEY_RAIL_WIDTH, String(next))
        return next
      })
    },
    []
  )

  const setInitialDraft = useCallback((value: string) => {
    writeStorage(STORAGE_KEY_DRAFT, value || null)
    initialDraftRef.current = value
    setInitialDraftVersion((previous) => previous + 1)
  }, [])

  const setDraft = useCallback((value: string) => {
    initialDraftRef.current = value

    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current)
    }

    draftTimerRef.current = setTimeout(() => {
      writeStorage(STORAGE_KEY_DRAFT, value || null)
    }, DRAFT_DEBOUNCE_MS)
  }, [])

  const setInitialMentions = useCallback((value: MentionItem[]) => {
    writeStorage(STORAGE_KEY_MENTIONS, JSON.stringify(value))
    initialMentionsRef.current = value
    setInitialDraftVersion((previous) => previous + 1)
  }, [])

  const setMentions = useCallback((next: MentionItem[]) => {
    initialMentionsRef.current = next
    writeStorage(
      STORAGE_KEY_MENTIONS,
      next.length > 0 ? JSON.stringify(next) : null
    )
  }, [])

  useEffect(
    () => () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current)
      }
    },
    []
  )

  return {
    isOpen,
    setIsOpen,
    railWidth,
    setRailWidth,
    currentConversationId,
    setCurrentConversationId,
    initialDraft: initialDraftRef.current,
    setInitialDraft,
    setDraft,
    setInitialMentions,
    initialMentions: initialMentionsRef.current,
    setMentions,
    initialDraftVersion,
  }
}
